import React from "react";
import EdgeContent from "./EdgeContent";
import IEdgeData from "../../database/interfaces/IEdgeData";
import {DatabaseContext} from "../../database/DatabaseHandler";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {IDecorParamTypeDescriptor} from "../../database/interfaces/paramScript/IParamTypeDescriptor";
import {ALL_EDGE_KEYS, SIDE_KEYS, SIDE_TOKENS, TOKEN_TO_FIELD, VISIBILITY_KEYS, VISIBILITY_TOKENS, shownSideTokens, shownVisibilityTokens, ZERO_GUID} from "./edgeKeys";
import { isEdgeAvailable } from "./edgeAvailability";

// Hány külön célt tud a paramValue EGYÁLTALÁN tárolni: 4 oldal- + 3 láthatóság-mező. Ez NEM a
// felvehető bejegyzések száma (az a lap oldalainak száma, lásd `maxEdges`) — ez csak a betöltéskori
// újraépítés védőhálója, hogy egy korábban túltöltött mentésből se vesszen el tárolt adat.
const REPRESENTABLE_EDGE_TARGETS = ALL_EDGE_KEYS.length;

/**
 * Edge list property content component.
 */
interface IEdgeListContentProps {
    typeDescriptor: IDecorParamTypeDescriptor;
    paramValue: any;
    compatibleEdgeIds: string[];
    onUpdateParamValue: (value: any) => void;
}

// Egy él-bejegyzés módja: vagy oldal-alapú, vagy láthatóság-alapú. Üres (még nem választott)
// bejegyzésnél null — ilyenkor MINDKÉT gomb-csoport aktív, az első kattintás rögzíti a módot.
export type EdgeMode = "sides" | "visibility";

export type EdgeProductsType = {
    edge: IEdgeData,
    mode: EdgeMode | null,
    selections: string[]
}

/** A kijelölt tokenekből megállapítja a módot (a két token-halmaz diszjunkt). */
const deriveMode = (selections: string[]): EdgeMode | null => {
    if (selections.length === 0) return null;
    return (SIDE_TOKENS as readonly string[]).includes(selections[0]) ? "sides" : "visibility";
};

const EdgeListContent: React.FC<IEdgeListContentProps> = ({ typeDescriptor, paramValue, compatibleEdgeIds, onUpdateParamValue }) => {
    const dataContext = React.useContext(DatabaseContext);

    // Tracks whether the initial build has happened.
    // After initial load, we don't auto-create an edge when all sides are removed.
    const hasInitialized = React.useRef(false);

    // Ha van duplung, annak vastagságát is hozzáadja.
    const requiredEdgeWidth = React.useMemo(() => {
        const board = dataContext.boardData?.findByID(paramValue.BoardProduct);
        const duplung = paramValue.DuplungProduct
            ? dataContext.boardData?.findByID(paramValue.DuplungProduct)
            : null;

        return board?.IsWorkTop
            ? (board?.SheetThickness ?? 0)
            : (board?.SheetThickness ?? 0) + (duplung?.SheetThickness ?? 0);
    }, [dataContext.boardData, paramValue.BoardProduct, paramValue.DuplungProduct]);

    // Felső szélesség-korlát:
    //  - duplung: nincs korlát (a requiredEdgeWidth = lap+duplung úgyis kizárja a keskenyeket,
    //    pl. 18+18=36 → csak a 43-asok jönnek);
    //  - worktop: 44 (a 38mm+ élek, köztük a 43-asok kellenek);
    //  - sima lap duplung nélkül: 30 → a 22/23-as élek beférnek, a 43-asok KIESNEK.
    const maxEdgeWidth = React.useMemo(() => {
        if (paramValue.DuplungProduct) return null;
        const board = dataContext.boardData?.findByID(paramValue.BoardProduct);
        return board?.IsWorkTop ? 44 : 30;
    }, [dataContext.boardData, paramValue.BoardProduct, paramValue.DuplungProduct]);

    // Worktop (munkalap) board? Egyetlen forrás: a board IsWorkTop flagje.
    const isWorkTop = React.useMemo(
        () => !!dataContext.boardData?.findByID(paramValue.BoardProduct)?.IsWorkTop,
        [dataContext.boardData, paramValue.BoardProduct]
    );

    // A megjeleníthető oldalak. Worktopnál a Top ("front") oldal-GOMB kimarad.
    // FONTOS: ez KIZÁRÓLAG kozmetikai (gomb-elrejtés) — semmilyen él-ADATOT nem módosít,
    // az auto-él kiválasztás / board-váltás / szélesség-logika teljesen érintetlen.
    const allowedSides = React.useMemo(
        () => shownSideTokens(typeDescriptor.edge?.sideLabels, isWorkTop),
        [typeDescriptor.edge?.sideLabels, isWorkTop]
    );

    // A megjeleníthető láthatóság-gombok — ugyanaz a descriptor-vezérelt szabály, mint az oldalaknál.
    const allowedVisibilities = shownVisibilityTokens(typeDescriptor.edge?.visibilityLabels);

    // Hány él-bejegyzés lehet legfeljebb: ahány ÉLE van a lapnak — sima lapnál 4, munkalapnál 3.
    // A két token-halmaz NEM adódik össze (korábban az összegük, 4 + 3 = 7 volt a határ); a
    // láthatóság-gombok ugyanazokat az oldalakat csoportosítják át, nem adnak újabb célt.
    // Ha az aktív csoportban épp nincs szabad hely, az új bejegyzés tokenek nélkül jön létre — ez
    // szándékos: a felhasználó dönti el, melyik csoportba kerüljön (lásd handleAddNewEdge).
    const maxEdges = allowedSides.length;

    /**
     * Ugyanaz a szűrő logika mint az EdgeContent-ben lévő edgeFilter.
     * Ezt használjuk a handleAddNewEdge-ben, hogy ne válasszunk olyan élet
     * amit aztán a DecorSelectorInput kiszűr és lecserél valami másra.
     */
    const edgePassesFilter = React.useCallback((edge: IEdgeData): boolean => {
        // Csak elérhető (Status = 1) élek — összhangban az EdgeContent listájával és az auto-választással.
        if (!isEdgeAvailable(edge)) return false;
        if (edge.Width < requiredEdgeWidth) return false;
        if (maxEdgeWidth !== null && edge.Width > maxEdgeWidth) return false;

        const filters = typeDescriptor.edge?.filters;
        if (!filters) return true;

        if (filters.oriented !== undefined) {
            if (filters.oriented ? edge.Oriented != 1 : edge.Oriented != 0) return false;
        }

        if (filters.thickness !== undefined) {
            if (edge.Thickness < filters.thickness.min || edge.Thickness > filters.thickness.max) return false;
        }

        return true;
    }, [requiredEdgeWidth, maxEdgeWidth, typeDescriptor.edge?.filters]);

    /**
     * A merged él-bejegyzésekből felépíti a paramValue mind a 7 él-mezőjét.
     * Minden mezőt ZERO_GUID-ra állít, majd a bejegyzések tokenjei szerint kitölti.
     * Single source of truth: a UI-állapot → a paramValue.
     */
    const rebuildScript = React.useCallback((base: any, merged: EdgeProductsType[]): any => {
        const script = { ...base };
        ALL_EDGE_KEYS.forEach(field => { script[field] = ZERO_GUID; });
        for (const ep of merged) {
            for (const token of ep.selections) {
                const field = TOKEN_TO_FIELD[token];
                if (field) script[field] = ep.edge.ID;
            }
        }
        return script;
    }, []);

    /** Két bejegyzést összevon, ha AZONOS módban AZONOS él-ID-ra mutatnak (token-union). */
    const mergeByModeAndId = React.useCallback((list: EdgeProductsType[]): EdgeProductsType[] => {
        const merged: EdgeProductsType[] = [];
        const keyToIndex = new Map<string, number>();
        for (const ep of list) {
            const key = `${ep.mode}|${ep.edge.ID}`;
            const idx = keyToIndex.get(key);
            if (idx !== undefined) {
                const existing = merged[idx];
                merged[idx] = {
                    edge: existing.edge,
                    mode: existing.mode,
                    selections: Array.from(new Set([...existing.selections, ...ep.selections]))
                };
            } else {
                keyToIndex.set(key, merged.length);
                merged.push({ edge: ep.edge, mode: ep.mode, selections: [...ep.selections] });
            }
        }
        return merged;
    }, []);

    const [edgeProducts, setEdgeProducts] = React.useState<Array<EdgeProductsType>>();
    // Initialize the edge products state based on the current paramValue
    React.useEffect(() => {
        // A paramValue él-mezői csak azt hordozzák, MELYIK célon (oldal / láthatóság) melyik él van,
        // a selectorok VIZUÁLIS SORRENDJÉT nem. Ezért a sorrendet a meglévő edgeProducts (prev)
        // alapján őrizzük meg, és csak az egyes selectorok selections-jét frissítjük a paramValue-ból.

        // Cél → tokenek leképezés módonként (a két csoport diszjunkt → külön map). Ezek CSAK azt
        // mondják meg, melyik él-ID melyik tokeneket kapja; a SORRENDET a prev adja (lásd lent).
        const buildSelById = (fieldTokenPairs: ReadonlyArray<readonly [string, string]>): Record<string, string[]> => {
            const map: Record<string, string[]> = {};
            fieldTokenPairs.forEach(([field, token]) => {
                const id = paramValue[field];
                if (id && id !== ZERO_GUID) {
                    map[id] = map[id] || [];
                    map[id].push(token);
                }
            });
            return map;
        };

        const sidePairs = SIDE_KEYS.map((field, i) => [field, SIDE_TOKENS[i]] as const);
        const visPairs = VISIBILITY_KEYS.map((field, i) => [field, VISIBILITY_TOKENS[i]] as const);
        const sidesById = buildSelById(sidePairs);
        const visById = buildSelById(visPairs);

        setEdgeProducts(prev => {
            // EGYSÉGES, prev-sorrendet megőrző rekonstrukció. A prev-et a SAJÁT sorrendjében járjuk
            // végig, és minden selectort A HELYÉN frissítünk a saját módja szerint. Ezért:
            //  - egy kiürülő selector (utolsó tokenjét levették) a HELYÉN marad üresen (mode null),
            //    NEM ugrik a lista aljára — ezt rontotta el a korábbi "két bucket + üres-farok" forma;
            //  - oldal- és láthatóság-bejegyzések eredeti, akár váltakozó sorrendje is megmarad.
            const prevList = prev ?? [];
            const products: EdgeProductsType[] = [];
            const seenSide = new Set<string>();
            const seenVis = new Set<string>();

            // Board-váltás remap: a paramValue-ben szereplő, de a prev azonos-módú bejegyzéseiben még
            // NEM látott él-ID-k → ezeket a kiürült azonos-módú selectorokra remap-eljük, hogy a
            // selector identitása (pozíció + tokenek) megmaradjon.
            const prevSideIds = new Set(prevList.filter(e => e.mode === "sides").map(e => e.edge.ID));
            const prevVisIds = new Set(prevList.filter(e => e.mode === "visibility").map(e => e.edge.ID));
            const newSideIds = Object.keys(sidesById).filter(id => !prevSideIds.has(id));
            const newVisIds = Object.keys(visById).filter(id => !prevVisIds.has(id));
            const consumedSide = new Set<string>();
            const consumedVis = new Set<string>();

            const refresh = (
                ep: EdgeProductsType,
                mode: EdgeMode,
                byId: Record<string, string[]>,
                seen: Set<string>,
                newIds: string[],
                consumed: Set<string>
            ): EdgeProductsType => {
                if (byId[ep.edge.ID] !== undefined) {
                    seen.add(ep.edge.ID);
                    return { edge: ep.edge, mode, selections: byId[ep.edge.ID] };
                }
                const remapId = newIds.find(id => !consumed.has(id));
                const remapEdge = remapId ? dataContext.edgeData?.findByID(remapId) : undefined;
                if (remapId && remapEdge) {
                    consumed.add(remapId);
                    seen.add(remapId);
                    return { edge: remapEdge, mode, selections: byId[remapId] };
                }
                // Nincs mire remap-elni → kiürült selector, a HELYÉN marad (mode null).
                return { edge: ep.edge, mode: null, selections: [] };
            };

            // 1) prev a saját sorrendjében, minden selector a helyén frissül a módja szerint.
            prevList.forEach(ep => {
                if (ep.mode === "sides") {
                    products.push(refresh(ep, "sides", sidesById, seenSide, newSideIds, consumedSide));
                } else if (ep.mode === "visibility") {
                    products.push(refresh(ep, "visibility", visById, seenVis, newVisIds, consumedVis));
                } else {
                    // ÜRES (mode null) selector: erre IS érvényes a remap, csak mód nélkül indul,
                    // ezért mindkét csoportot megnézzük. Enélkül a board-váltás / reset után érkező
                    // él-ID a lista VÉGÉRE került ÚJ sorként, miközben ez a sor üresen maradt — így
                    // jelent meg egy fölösleges, a keretet fogyasztó bejegyzés a régi mellett.
                    if (newSideIds.some(id => !consumedSide.has(id))) {
                        products.push(refresh(ep, "sides", sidesById, seenSide, newSideIds, consumedSide));
                    } else if (newVisIds.some(id => !consumedVis.has(id))) {
                        products.push(refresh(ep, "visibility", visById, seenVis, newVisIds, consumedVis));
                    } else {
                        products.push({ edge: ep.edge, mode: null, selections: [] });
                    }
                }
            });

            // 2) A paramValue-ben szereplő, prev-ben még nem látott (és remap-re sem használt) él-ID-k
            //    a lista VÉGÉRE kerülnek (oldal-csoport, majd láthatóság) — ez a beszúrási sorrend.
            Object.keys(sidesById).forEach(id => {
                if (seenSide.has(id)) return;
                const edgeData = dataContext.edgeData?.findByID(id);
                if (edgeData) { products.push({ edge: edgeData, mode: "sides", selections: sidesById[id] }); seenSide.add(id); }
            });
            Object.keys(visById).forEach(id => {
                if (seenVis.has(id)) return;
                const edgeData = dataContext.edgeData?.findByID(id);
                if (edgeData) { products.push({ edge: edgeData, mode: "visibility", selections: visById[id] }); seenVis.add(id); }
            });

            if (!hasInitialized.current && products.length === 0) {
                const firstEdge = dataContext.edgeData?.first();
                if (firstEdge) {
                    products.push({ edge: firstEdge, mode: null, selections: [] });
                }
            }

            hasInitialized.current = true;

            // TOKEN NÉLKÜLI sorok eldobása. Egy bejegyzés, amihez egyetlen oldal/láthatóság sem
            // tartozik, semmit nem csinál: a paramValue nem is tárolja, a Unity felé nem megy ki.
            // Ilyen akkor keletkezik, ha egy ÚJ paramValue elveszi a sor tokenjeit — pl. a reset
            // gombnál, ami a gyári állapotot írja be (egy lap + egy él). Ott a régi sorok üresen
            // maradtak volna a kártyán, holott már nincs mögöttük adat.
            //
            // KIVÉTEL: ha így egy sor sem maradna, az utolsót megtartjuk — a kártyán mindig legyen
            // egy selector (üres kártyán a lenti `hasInitialized` ág is ezért tesz be egyet).
            // A "+"-szal, betelt csoportba felvett üres sort ez NEM érinti: az csak a lokális
            // állapotba kerül (a parent frissítése elmarad), tehát ez az effekt le sem fut rá.
            const withTokens = products.filter(p => p.selections.length > 0);
            if (withTokens.length > 0 && withTokens.length < products.length) {
                products.length = 0;
                products.push(...withTokens);
            }
            // Védőháló: a paramValue ennyi célt tud reprezentálni. A tokennel rendelkező
            // bejegyzések elsőbbséget élveznek (azokat a paramValue tényleg tárolja).
            if (products.length > REPRESENTABLE_EDGE_TARGETS) {
                const withSel = products.filter(p => p.selections.length > 0);
                const withoutSel = products.filter(p => p.selections.length === 0);
                return [...withSel, ...withoutSel].slice(0, REPRESENTABLE_EDGE_TARGETS);
            }

            console.log("EDGELIST REBUILD", {
                paramTop: paramValue.TopEdgeProduct,
                paramBottom: paramValue.BottomEdgeProduct,
                paramLeft: paramValue.LeftEdgeProduct,
                paramRight: paramValue.RightEdgeProduct,
                products: products.map(p => ({
                    edgeId: p.edge.ID,
                    edgeName: p.edge.Name,
                    mode: p.mode,
                    selections: p.selections,
                })),
                sidesById,
                visById,
            });

            console.log("REBUILD", {
                paramTop: paramValue.TopEdgeProduct,
                paramLeft: paramValue.LeftEdgeProduct,
                products: products.map(p => ({ name: p.edge.Name, id: p.edge.ID, sel: p.selections })),
            });

            return products;
        });
    }, [
        dataContext.edgeData,
        paramValue.TopEdgeProduct,
        paramValue.BottomEdgeProduct,
        paramValue.LeftEdgeProduct,
        paramValue.RightEdgeProduct,
        paramValue.VisibleEdgeProduct,
        paramValue.JointEdgeProduct,
        paramValue.HiddenEdgeProduct,
    ]);

    // Ha requiredEdgeWidth vagy maxEdgeWidth változik (pl. duplung toggle),
    // a tokenhez NEM rendelt éleket is frissítjük a megfelelő szélességűre.
    React.useEffect(() => {
        if (!hasInitialized.current) return;

        setEdgeProducts(prev => {
            if (!prev) return prev;
            let changed = false;
            const updated = prev.map(ep => {
                // Csak a tokenhez NEM rendelt éleket frissítjük
                if (ep.selections.length > 0) return ep;
                // Ha átmegy a szűrőn, békén hagyjuk
                if (edgePassesFilter(ep.edge)) return ep;

                // Ugyanolyan dekórú élet keresünk ami átmegy
                const sameDecor = dataContext.edgeData?.children()
                    .find(e => e.ProductImage === ep.edge.ProductImage && edgePassesFilter(e));
                if (sameDecor) {
                    changed = true;
                    return { ...ep, edge: sameDecor };
                }
                // Fallback: bármely él ami átmegy
                const anyEdge = dataContext.edgeData?.children().find(e => edgePassesFilter(e));
                if (anyEdge) {
                    changed = true;
                    return { ...ep, edge: anyEdge };
                }
                return ep;
            });
            return changed ? updated : prev;
        });
    }, [requiredEdgeWidth, maxEdgeWidth, edgePassesFilter, dataContext.edgeData]);

    // Board-DEKÓR váltásakor (lap-választás VAGY reset) a tokenhez NEM rendelt bejegyzések is
    // átváltanak az új dekórra. A hozzárendelt éleket a paramValue viszi (lap-váltásnál
    // edgeAutoSelect.replaceCompatEdgesToNewDecor, resetnél paramDefaults.decorDefault), a tokenhez
    // nem rendelt sorok viszont CSAK ebben a state-ben élnek — eddig a régi dekóron maradtak.
    // Kizárólag AZONOS VASTAGSÁGRA váltunk: ha az új dekórból épp az a vastagság hiányzik, a sor
    // marad, ami volt (különben két külön vastagságú sor ugyanarra az élre esne, és a lista
    // egyetlen sorrá vonná őket össze).
    const prevBoardIdRef = React.useRef<string | undefined>(paramValue.BoardProduct);
    React.useEffect(() => {
        const prevBoardId = prevBoardIdRef.current;
        prevBoardIdRef.current = paramValue.BoardProduct;
        if (!prevBoardId || prevBoardId === paramValue.BoardProduct) return;

        const oldDecor = dataContext.boardData?.findByID(prevBoardId)?.ProductImage;
        const newDecor = dataContext.boardData?.findByID(paramValue.BoardProduct)?.ProductImage;
        if (!oldDecor || !newDecor || oldDecor === newDecor) return;

        // Az ÚJ lap SAJÁT dekórjának érvényes élei, a lap kompatibilis listájából — így a bejegyzés
        // a selector opciói közül kap értéket, és a dekór-párok idegen dekórjai nem jöhetnek szóba.
        const newDecorEdges = compatibleEdgeIds
            .map(id => dataContext.edgeData?.findByID(id))
            .filter((e): e is IEdgeData => !!e && e.ProductImage === newDecor && edgePassesFilter(e));
        if (newDecorEdges.length === 0) return;

        setEdgeProducts(prev => {
            if (!prev) return prev;
            let changed = false;
            const updated = prev.map(ep => {
                if (ep.selections.length > 0) return ep;           // hozzárendelt: a paramValue viszi
                if (ep.edge.ProductImage !== oldDecor) return ep;  // idegen dekór: marad
                const sameThickness = newDecorEdges.find(e => e.Thickness === ep.edge.Thickness);
                if (!sameThickness || sameThickness.ID === ep.edge.ID) return ep;
                changed = true;
                return { ...ep, edge: sameThickness };
            });
            return changed ? updated : prev;
        });
    }, [paramValue.BoardProduct, compatibleEdgeIds, dataContext.boardData, dataContext.edgeData, edgePassesFilter]);

    // Load-idejű él-javítás (MINDEN élzárható board). Board-VÁLTÁSKOR a BoardDecorCardContent már
    // auto-választ érvényes élt, de RELOAD-kor / egy addig REJTETT (visibleWhen) kártya ELSŐ
    // MOUNT-jakor ez nem fut le — a mentett / default paramValue éllel jövünk. Ha a hozzárendelt él
    // kiesik a szűrőn (méret VAGY elérhetőség: pl. a shop-default éle időközben KIFUTÓ lett, Status≠1),
    // a value nincs az opciók közt → a combobox üresen látszik. Itt az ilyen hibás/nem-elérhető éleket
    // auto-cseréljük egy ÉRVÉNYES, ELÉRHETŐ élre — a board-váltással azonos szellemben.
    //
    // Korábban ez CSAK worktopra futott; kiterjesztve minden EdgeBandable boardra, mert a default
    // board éle bármely dekornál kifuthat (a configurator csak Status=1 élt kínál). A csere VASTAGSÁG
    // szerint csökkenő preferenciájú (2mm → 0.8 → 0.4 → …): a legvastagabb ELÉRHETŐ, azonos-dekórú élt
    // választja; ha nincs, a board kompatibilis éleiből a legvastagabbat; végül bármely érvényeset.
    // Így pl. a Barna Kansasnál (ahol már csak a 0.4mm elérhető) a 0.4mm-es él töltődik be — nem üres.
    React.useEffect(() => {
        const board = dataContext.boardData?.findByID(paramValue.BoardProduct);
        if (!board || board.EdgeBandable != 1 || !dataContext.edgeData) return;

        // Hozzárendelt (nem-ZERO) élek, amik NEM mennek át a board méret-/elérhetőség-szűrőjén.
        const invalidKeys = ALL_EDGE_KEYS.filter(k => {
            const id = paramValue[k];
            if (!id || id === ZERO_GUID) return false;
            const e = dataContext.edgeData?.findByID(id);
            return !e || !edgePassesFilter(e);
        });
        if (invalidKeys.length === 0) return;

        // Vastagság szerint csökkenő: a legvastagabb (2mm) preferált, majd 0.8, majd a maradék (0.4…).
        const byThicknessDesc = (a: IEdgeData, b: IEdgeData) => b.Thickness - a.Thickness;

        // Csere-él, dekór-megtartással + vastagság-preferenciával: 1) azonos dekórú (ProductImage)
        // érvényes él, 2) a board kompatibilis éle, 3) bármely érvényes él. Mindhárom GARANTÁLTAN
        // átmegy az edgePassesFilter-en → benne lesz a combobox opcióiban (nincs végtelen ciklus).
        const firstInvalidEdge = dataContext.edgeData.findByID(paramValue[invalidKeys[0]]);
        const sameDecorValid = firstInvalidEdge
            ? dataContext.edgeData.children().filter(e => e.ProductImage === firstInvalidEdge.ProductImage && edgePassesFilter(e)).sort(byThicknessDesc)[0]
            : undefined;
        const compatValid = (board.CompatibleEdgings ?? [])
            .map(id => dataContext.edgeData?.findByID(id))
            .filter((e): e is IEdgeData => !!e && edgePassesFilter(e))
            .sort(byThicknessDesc)[0];
        const anyValid = dataContext.edgeData.children().filter(e => edgePassesFilter(e)).sort(byThicknessDesc)[0];
        const replacement = sameDecorValid ?? compatValid ?? anyValid;
        if (!replacement) return; // nincs érvényes él → ne loopoljunk

        // Loop-védelem: ha a hibás mezők már mind a csere-élre mutatnak, nincs teendő.
        if (invalidKeys.every(k => paramValue[k] === replacement.ID)) return;

        const newScript = { ...paramValue };
        invalidKeys.forEach(k => { newScript[k] = replacement.ID; });
        onUpdateParamValue(newScript);
    }, [
        dataContext.boardData,
        dataContext.edgeData,
        edgePassesFilter,
        paramValue.BoardProduct,
        paramValue.TopEdgeProduct,
        paramValue.BottomEdgeProduct,
        paramValue.LeftEdgeProduct,
        paramValue.RightEdgeProduct,
        paramValue.VisibleEdgeProduct,
        paramValue.JointEdgeProduct,
        paramValue.HiddenEdgeProduct,
    ]);

    const handleDecorChange = (index: number, newId: string | null, currentSelections: string[]) => {
        if (newId == null || newId === ZERO_GUID) return;

        const newEdgeData = dataContext.edgeData?.findByID(newId);
        if (!newEdgeData) {
            console.error("Edge data not found for ID while changing decor:", index, newId);
            return;
        }

        // 1. Az adott entry dekorját átírjuk; a mód és a tokenek (selections) megmaradnak.
        const newEdgeProducts = edgeProducts ? [...edgeProducts] : [];
        const mode = newEdgeProducts[index]?.mode ?? deriveMode(currentSelections);
        newEdgeProducts[index] = { edge: newEdgeData, mode, selections: currentSelections };

        // 2. MERGE: ha a dekor-váltás után két AZONOS MÓDÚ entry ugyanarra az él-ID-ra mutat,
        //    összevonjuk őket. (Más módú azonos él-ID NEM olvad össze → "kevert nem lehet".)
        const merged = mergeByModeAndId(newEdgeProducts);

        const newScript = rebuildScript(paramValue, merged);
        setEdgeProducts(merged);
        onUpdateParamValue(newScript);
    };

    const handleSelectionChange = (index: number, id: string, newSelections: string[]) => {
        // 1. A kattintott entry tokenjeit frissítjük; a mód a tokenekből adódik (üresnél null).
        const newEdgeProducts = edgeProducts ? [...edgeProducts] : [];
        const activeMode = deriveMode(newSelections);
        newEdgeProducts[index] = {
            edge: newEdgeProducts[index].edge,
            mode: activeMode,
            selections: newSelections
        };
        // Exkluzivitás a TÖBBI entryn. Két szabály:
        //  a) egy cél = egy anyag → a most lefoglalt tokeneket máshonnan kivesszük;
        //  b) GLOBÁLIS mód-kapcsoló → ha a kattintás egy csoportot AKTÍVVÁ tett (activeMode),
        //     a teljes ELLENTÉTES csoport minden tokenjét kivesszük MINDEN más entryből is
        //     ("vagy az oldalak, vagy a láthatóság — az egész kártyán"). Ha a kattintás épp
        //     kikapcsolt egy tokent (activeMode === null), a másik csoportot békén hagyjuk.
        newEdgeProducts.forEach((ep, i) => {
            if (i === index) return;
            const filtered = ep.selections.filter(s => {
                if (newSelections.includes(s)) return false;                  // (a)
                if (activeMode && deriveMode([s]) !== activeMode) return false; // (b)
                return true;
            });
            if (filtered.length !== ep.selections.length) {
                newEdgeProducts[i] = { edge: ep.edge, mode: deriveMode(filtered), selections: filtered };
            }
        });

        // 2. Azonos módú + azonos él-ID entryk összevonása (token-union).
        const merged = mergeByModeAndId(newEdgeProducts);

        // 3. A merged állapotból újraépítjük a scriptet (single source of truth).
        const newScript = rebuildScript(paramValue, merged);
        setEdgeProducts(merged);
        onUpdateParamValue(newScript);
    };

    const handleAddNewEdge = () => {
        if ((edgeProducts?.length ?? 0) >= maxEdges) return;

        const board = dataContext.boardData?.findByID(paramValue.BoardProduct || ZERO_GUID);

        // A kártya AKTÍV módja dönti el, melyik csoportba kerül az új él (Q: "Match the card's
        // active group"). A "nincs keverés" szabály miatt egyszerre csak az egyik csoport aktív:
        //  - ha (kizárólag) láthatóság-mező van beállítva → az új él is LÁTHATÓSÁG-módú lesz,
        //    és a szabad láthatóság-helyekre (látható/takart/rejtett) kerül — így nem keveredik
        //    oldal-élekkel (ez volt a hiba: az üres oldalak auto-kitöltése kevert kártyát csinált);
        //  - egyébként (oldal aktív VAGY üres kártya) marad a régi viselkedés: szabad OLDALAK.
        const isFieldSet = (f: string) => { const v = paramValue[f]; return !!v && v !== ZERO_GUID; };
        const sideActive = SIDE_KEYS.some(isFieldSet);
        const visActive = VISIBILITY_KEYS.some(isFieldSet);
        const useVisibility = visActive && !sideActive;

        // Az aktív csoport mezői / tokenjei (oldalakra VAGY láthatóságra). A szabad-hely kiosztás
        // és a tokenek innen jönnek, így a logika szimmetrikus a két csoportra.
        const groupKeys: readonly string[] = useVisibility ? VISIBILITY_KEYS : SIDE_KEYS;
        const groupTokens: readonly string[] = useVisibility ? VISIBILITY_TOKENS : SIDE_TOKENS;
        const groupMode: EdgeMode = useVisibility ? "visibility" : "sides";

        // Szabad cél-helyek az aktív csoportban = jelenleg élhez NEM rendelt mezők (ZERO_GUID).
        const freeIndices = groupKeys
            .map((field, index) => (isFieldSet(field) ? -1 : index))
            .filter(index => index >= 0);

        // "Már használt" élek: MINDEN él-mező (oldalak + láthatóság) + a jelen lévő selectorok ID-jai.
        // Így a "+" nem választja újra ugyanazt → nincs duplikáció.
        const assigned = new Set<string>([
            ...ALL_EDGE_KEYS.map(k => paramValue[k]).filter(e => e && e !== ZERO_GUID),
            ...(edgeProducts?.map(ep => ep.edge.ID) ?? []),
        ]);

        // Board-kompatibilis (azonos dekór) élek, méret/szűrő szerint átengedve.
        const compatible = (board?.CompatibleEdgings ?? [])
            .map(id => dataContext.edgeData?.findByID(id))
            .filter((e): e is IEdgeData => !!e && edgePassesFilter(e));

        // A nagy-listás fallbackhez rendezett másolat: név szerint ABC, azonos névnél
        // a vastagabb (Thickness csökkenő) előbb — UGYANAZ a sorrend mint az EdgeContent
        // legördülő listájában, hogy a "+" determinisztikusan és a listával konzisztensen válasszon.
        const sortedAllEdges = [...(dataContext.edgeData?.children() ?? [])].sort((a, b) => {
            const nameCompare = a.Name.localeCompare(b.Name);
            if (nameCompare !== 0) return nameCompare;
            return b.Thickness - a.Thickness;
        });

        // Él-választás preferencia-sorrend (változatlan):
        //  1) board-kompatibilis (azonos dekór) ami MÉG NINCS használatban,
        //  2) ha minden compatible fent van → a nagy listából a névsorban első méretben passzoló,
        //     ami még nincs használatban,
        //  3) graceful fallback: bármely méretben passzoló él.
        const picked =
            compatible.find(e => !assigned.has(e.ID)) ??
            sortedAllEdges.find(e => edgePassesFilter(e) && !assigned.has(e.ID)) ??
            sortedAllEdges.find(e => edgePassesFilter(e));

        if (!picked) {
            console.error("No suitable edge found while adding new edge.");
            return;
        }

        // Az új élt az aktív csoport MINDEN szabad helyére rendeljük (oldalaknál az összes szabad
        // oldal, láthatóságnál az összes szabad látható/takart/rejtett — szimmetrikus). Ha nincs
        // szabad hely, tokenek nélkül (mode: null) jön létre — a user dönti el a csoportot.
        const newScript = { ...paramValue };
        const newSelections: string[] = [];
        freeIndices.forEach(index => {
            newScript[groupKeys[index]] = picked.ID;
            newSelections.push(groupTokens[index]);
        });

        // Lokálisan hozzáadjuk az új selectort (a "minden hely foglalt" eset miatt: olyankor
        // szándékosan NEM hívjuk a parent-et, így a useEffect nem törli ezt az üres selectort).
        setEdgeProducts(prev => [
            ...(prev ?? []),
            { edge: picked, mode: newSelections.length > 0 ? groupMode : null, selections: newSelections }
        ]);

        // A parent-et (single source of truth) csak akkor frissítjük, ha tényleg foglaltunk helyet.
        if (newSelections.length > 0) {
            onUpdateParamValue(newScript);
        }
    }

    const handleRemoveEdge = (index: number) => {
        const newScript = { ...paramValue };
        const edgeToRemove = edgeProducts ? edgeProducts[index] : null;
        if (edgeToRemove) {
            // Csak a TÖRÖLT bejegyzés tokenjeihez tartozó mezőket nullázzuk — így ha ugyanaz az
            // él-anyag egy másik (más módú) bejegyzésben is szerepel, az érintetlen marad.
            edgeToRemove.selections.forEach(token => {
                const field = TOKEN_TO_FIELD[token];
                if (field) newScript[field] = ZERO_GUID;
            });
        }
        // Update edge list state
        const newEdgeProducts = edgeProducts ? [...edgeProducts] : [];
        newEdgeProducts.splice(index, 1);
        setEdgeProducts(newEdgeProducts);
        // Update parameters
        onUpdateParamValue(newScript);
    }

    const isEdgeNotFound = (token: string) => {
        if (token === "top" && paramValue.TopEdgeNotFound) return true;
        if (token === "bottom" && paramValue.BottomEdgeNotFound) return true;
        if (token === "left" && paramValue.LeftEdgeNotFound) return true;
        if (token === "right" && paramValue.RightEdgeNotFound) return true;

        return false;
    }

    // A láthatóság-gombok címkéi. Csak akkor adjuk tovább (és csak akkor jelennek meg az EdgeContent-ben),
    // ha a descriptor opt-in-elt rájuk a visibilityLabels-szel. ("nem mindenhol kell látszania")
    // MELY láthatóság-gombok látszanak — ugyanaz a descriptor-vezérelt szűrés, mint az oldalaknál.
    // A `visibilityLabels` továbbra is a be/ki kapcsoló (hiányában nincs csoport); ez azon belül szűkít.

    const visibilityLabels: string[] | null = typeDescriptor.edge?.visibilityLabels
        ? [
            typeDescriptor.edge.visibilityLabels.visible || "Látható",
            typeDescriptor.edge.visibilityLabels.joint || "Kötés",
            typeDescriptor.edge.visibilityLabels.hidden || "Rejtett",
        ]
        : null;

    return edgeProducts && typeDescriptor.edge && (
        <Stack direction="column" spacing={1} width="100%">
            {edgeProducts.map(({ edge, selections }, index) => {
                if (!edge) return null;
                return (
                    <EdgeContent
                        key={`${edge.ID}-${index}`}
                        edge={edge}
                        labels={[
                            typeDescriptor.edge?.sideLabels?.top || "Felső",
                            typeDescriptor.edge?.sideLabels?.bottom || "Alsó",
                            typeDescriptor.edge?.sideLabels?.left || "Bal",
                            typeDescriptor.edge?.sideLabels?.right || "Jobb"
                        ]}
                        allowedSides={allowedSides}
                        visibilityLabels={visibilityLabels}
                        allowedVisibilities={allowedVisibilities}
                        selections={selections}
                        format={typeDescriptor.edge?.format || null}
                        filters={typeDescriptor.edge?.filters || null}
                        tooltip={typeDescriptor.edge?.tooltip || ""}
                        editable={typeDescriptor.editable === true && typeDescriptor.edge?.editable === true}
                        readOnly={typeDescriptor.readOnly === true || typeDescriptor.edge?.readOnly === true}
                        notFound={selections.some(s => isEdgeNotFound(s))}
                        requiredEdgeWidth={requiredEdgeWidth}
                        maxEdgeWidth={maxEdgeWidth}
                        compatibleEdgeIds={compatibleEdgeIds}
                        onDecorChange={(newValue, selections) => handleDecorChange(index, newValue, selections)}
                        onSelectionChange={(id, newSelections) => handleSelectionChange(index, id, newSelections)}
                        onRemoveEdge={() => handleRemoveEdge(index)}
                    />
                );
            })
            }
            {typeDescriptor.readOnly !== true && typeDescriptor.edge?.readOnly !== true && (
                <Stack direction="row" justifyContent="center">
                    <IconButton
                        disabled={!typeDescriptor.editable || !typeDescriptor.edge?.editable || edgeProducts.length >= maxEdges}
                        onClick={handleAddNewEdge}
                        tabIndex={-1}
                        size="small"
                        sx={{
                            color: '#424242',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '8px',
                            padding: '0.25rem 2rem',
                            '&:hover': {
                                backgroundColor: '#bdbdbd',
                            },
                        }}
                    >
                        <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                </Stack>
            )}
        </Stack>
    );
};

export default EdgeListContent;
